import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, ArrowRight, Check } from "lucide-react";
import Reveal from "./Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const REASONS = [
  "Embarcación abandonada",
  "Impago de amarre",
  "Reclamación de deuda",
  "Venta judicial",
  "Peritaje / tasación",
  "Otro",
];

const DIRECT_CONTACTS = [
  {
    name: "Leticia Martín-Montalvo Fernández",
    phone: "+34 639 537 102",
    email: "abogados@portsdebt.es",
  },
  {
    name: "José María del Carre Díaz-Gálvez",
    phone: "+34 610 447 259",
    email: "abogados@portsdebt.es",
  },
  {
    name: "Alfonso Ozores Leyún",
    phone: "+34 608 472 232",
    email: "alfonso@portsdebt.es",
  },
];

const initialState = {
  full_name: "",
  entity: "",
  role: "",
  email: "",
  phone: "",
  reason: "",
  message: "",
  privacy_accepted: false,
  "bot-field": "",
};

// Encode form data for Netlify Forms (URL-encoded body)
const encode = (data) =>
  Object.keys(data)
    .map(
      (k) =>
        encodeURIComponent(k) +
        "=" +
        encodeURIComponent(typeof data[k] === "boolean" ? (data[k] ? "yes" : "no") : data[k] ?? "")
    )
    .join("&");

// Modern floating-label input
const FloatField = ({
  id,
  name,
  label,
  type = "text",
  value,
  onChange,
  required = false,
  multiline = false,
  rows = 4,
  testId,
}) => {
  const [focused, setFocused] = useState(false);
  const isFloating = focused || (value && value.length > 0);

  const sharedClasses = `peer w-full bg-transparent border-b border-slate-300 text-ink text-[15px] pt-6 pb-2 px-0 outline-none transition-all duration-300 focus:border-navy ${
    multiline ? "resize-none" : ""
  }`;

  const InputEl = multiline ? "textarea" : "input";

  return (
    <div className="relative">
      <label
        htmlFor={id}
        className={`absolute left-0 pointer-events-none transition-all duration-300 origin-left ${
          isFloating
            ? "top-0 text-[11px] uppercase tracking-[0.18em] text-sky font-semibold"
            : "top-7 text-[15px] text-ink-soft"
        }`}
      >
        {label}
        {required && <span className="text-sky ml-0.5">*</span>}
      </label>
      <InputEl
        id={id}
        name={name}
        type={multiline ? undefined : type}
        rows={multiline ? rows : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        required={required}
        data-testid={testId}
        className={sharedClasses}
      />
      <div
        className={`absolute bottom-0 left-0 h-px bg-gradient-to-r from-sky to-navy transition-all duration-500 ${
          focused ? "w-full" : "w-0"
        }`}
      />
    </div>
  );
};

const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error("Debe aceptar la política de privacidad para enviar la consulta.");
      return;
    }
    if (!form.reason) {
      toast.error("Seleccione un motivo de la consulta.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...form }),
      });
      if (res.ok) {
        setSent(true);
        toast.success("Consulta enviada correctamente.");
        setForm(initialState);
      } else {
        toast.error("No se pudo enviar la consulta. Inténtelo de nuevo.");
      }
    } catch (err) {
      toast.error("Error al enviar la consulta. Revise los datos e inténtelo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="section-contact"
      className="py-24 sm:py-32 bg-white relative overflow-hidden"
    >
      <div className="absolute left-0 -top-8 font-display text-[14rem] lg:text-[18rem] leading-none text-navy/[0.025] select-none pointer-events-none -ml-6">
        07
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-sky" />
                <div className="text-xs uppercase tracking-[0.28em] text-sky font-semibold">
                  07 — Contacto
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.1] tracking-tight">
                ¿Tiene una embarcación{" "}
                <span className="italic text-navy/70">ocupando un amarre</span> sin resolución?
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 text-ink-muted text-base lg:text-lg leading-relaxed">
                Contacte con PORT&rsquo;S COLLECTORS para revisar el caso y valorar la vía más
                adecuada.
              </p>
            </Reveal>

            <div className="mt-12 space-y-5">
              {DIRECT_CONTACTS.map((c, idx) => (
                <Reveal key={c.name} delay={250 + idx * 80}>
                  <div
                    data-testid={`direct-contact-${idx}`}
                    className="group border-l-2 border-sky pl-5 py-1 hover:border-navy transition-colors"
                  >
                    <div className="font-display text-base text-navy leading-snug">
                      {c.name}
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-ink-muted">
                      {c.phone && (
                        <a
                          href={`tel:${c.phone.replace(/\s/g, "")}`}
                          className="flex items-center gap-2 hover:text-navy transition-colors"
                        >
                          <Phone size={13} strokeWidth={1.75} />
                          {c.phone}
                        </a>
                      )}
                      <a
                        href={`mailto:${c.email}`}
                        className="flex items-center gap-2 hover:text-navy transition-colors"
                      >
                        <Mail size={13} strokeWidth={1.75} />
                        {c.email}
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Right column - form */}
          <div className="lg:col-span-7">
            <Reveal delay={150}>
              {sent ? (
                <div
                  data-testid="contact-success"
                  className="relative bg-gradient-to-br from-navy-900 to-navy-800 text-white p-10 lg:p-14 rounded-sm overflow-hidden"
                >
                  <div className="absolute -top-1/3 -right-1/4 w-[60%] h-[140%] bg-gradient-to-bl from-sky-300/20 to-transparent blur-3xl pointer-events-none" />
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-sky text-navy-900 flex items-center justify-center">
                      <Check size={28} strokeWidth={2} />
                    </div>
                    <h3 className="mt-8 font-display text-3xl lg:text-4xl text-white leading-tight">
                      Consulta recibida.
                    </h3>
                    <p className="mt-5 text-white/80 text-base leading-relaxed max-w-md">
                      Gracias por contactar con PORT&rsquo;S COLLECTORS. Hemos recibido su consulta
                      y nuestro equipo la revisará a la mayor brevedad.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSent(false)}
                      data-testid="contact-send-another"
                      className="mt-10 inline-flex items-center gap-2 text-sky-300 hover:text-white transition-colors text-sm font-semibold group"
                    >
                      Enviar otra consulta
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  data-testid="contact-form"
                  className="relative bg-white border border-slate-200 hover:border-slate-300 transition-colors p-8 sm:p-12 rounded-sm shadow-[0_30px_60px_-30px_rgba(0,30,71,0.15)]"
                  noValidate
                >
                  {/* Required hidden fields for Netlify */}
                  <input type="hidden" name="form-name" value="contact" />
                  <p className="hidden">
                    <label>
                      No rellenar:{" "}
                      <input
                        name="bot-field"
                        value={form["bot-field"]}
                        onChange={(e) => update("bot-field", e.target.value)}
                      />
                    </label>
                  </p>

                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-sky via-navy to-sky" />

                  <div className="mb-10">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-sky font-semibold mb-2">
                      Formulario de consulta
                    </div>
                    <div className="font-display text-2xl text-navy leading-tight">
                      Cuéntenos su caso.
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                    <FloatField
                      id="full_name"
                      name="full_name"
                      label="Nombre y apellidos"
                      value={form.full_name}
                      onChange={(v) => update("full_name", v)}
                      required
                      testId="input-full-name"
                    />
                    <FloatField
                      id="entity"
                      name="entity"
                      label="Puerto / marina / entidad"
                      value={form.entity}
                      onChange={(v) => update("entity", v)}
                      required
                      testId="input-entity"
                    />
                    <FloatField
                      id="role"
                      name="role"
                      label="Cargo"
                      value={form.role}
                      onChange={(v) => update("role", v)}
                      testId="input-role"
                    />
                    <FloatField
                      id="phone"
                      name="phone"
                      label="Teléfono"
                      type="tel"
                      value={form.phone}
                      onChange={(v) => update("phone", v)}
                      testId="input-phone"
                    />
                    <div className="sm:col-span-2">
                      <FloatField
                        id="email"
                        name="email"
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={(v) => update("email", v)}
                        required
                        testId="input-email"
                      />
                    </div>

                    <div className="sm:col-span-2 relative">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-sky font-semibold pt-1">
                        Motivo de la consulta <span className="text-sky">*</span>
                      </div>
                      <Select
                        value={form.reason}
                        onValueChange={(v) => update("reason", v)}
                      >
                        <SelectTrigger
                          data-testid="select-reason"
                          className="mt-2 h-auto bg-transparent border-0 border-b border-slate-300 rounded-none px-0 pt-3 pb-2 text-[15px] text-ink hover:border-navy focus:ring-0 focus:border-navy data-[placeholder]:text-ink-soft transition-colors [&>svg]:text-ink-soft"
                        >
                          <SelectValue placeholder="Seleccione un motivo" />
                        </SelectTrigger>
                        <SelectContent className="border-slate-200">
                          {REASONS.map((r) => (
                            <SelectItem
                              key={r}
                              value={r}
                              data-testid={`select-reason-option-${r}`}
                              className="cursor-pointer focus:bg-surface-alt focus:text-navy"
                            >
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* Hidden field that Netlify will receive */}
                      <input type="hidden" name="reason" value={form.reason} />
                    </div>

                    <div className="sm:col-span-2">
                      <FloatField
                        id="message"
                        name="message"
                        label="Mensaje"
                        value={form.message}
                        onChange={(v) => update("message", v)}
                        required
                        multiline
                        rows={4}
                        testId="input-message"
                      />
                    </div>
                  </div>

                  <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <label
                      htmlFor="privacy"
                      className="flex items-start gap-3 cursor-pointer select-none group"
                    >
                      <span className="relative flex-shrink-0 mt-0.5">
                        <input
                          id="privacy"
                          name="privacy_accepted"
                          type="checkbox"
                          checked={form.privacy_accepted}
                          onChange={(e) => update("privacy_accepted", e.target.checked)}
                          data-testid="checkbox-privacy"
                          className="peer sr-only"
                        />
                        <span className="block w-5 h-5 border border-slate-400 group-hover:border-navy peer-checked:bg-navy peer-checked:border-navy transition-colors" />
                        <Check
                          size={12}
                          strokeWidth={3}
                          className="absolute top-1 left-1 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                        />
                      </span>
                      <span className="text-sm text-ink-muted leading-relaxed">
                        Acepto la{" "}
                        <a
                          href="/politica-de-privacidad"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-navy underline underline-offset-2 hover:text-sky"
                        >
                          política de privacidad
                        </a>
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="group relative inline-flex items-center justify-center gap-2 bg-navy text-white hover:text-navy-900 disabled:opacity-50 disabled:cursor-not-allowed px-8 py-4 rounded-md font-semibold text-sm tracking-wide transition-all duration-500 overflow-hidden whitespace-nowrap shadow-lg shadow-navy/20 hover:shadow-2xl hover:shadow-sky/40"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-sky-300 to-sky translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500 ease-out" />
                      <span className="relative z-10">
                        {submitting ? "Enviando..." : "Enviar consulta"}
                      </span>
                      {!submitting && (
                        <ArrowRight
                          size={16}
                          className="relative z-10 transition-transform group-hover:translate-x-1"
                        />
                      )}
                    </button>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
